import { skipToken } from '@apollo/client'
import { Check, ChevronsUpDown, RefreshCw } from 'lucide-react'
import { type ReactNode, Suspense, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router'
import { BranchSelectorFallback } from '@/components/form/branch-selector/fallback'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  AppBranchesQuery,
  AppBranchesQueryVariables,
  GitBranchesQuery,
  GitBranchesQueryVariables,
  StaticSiteBranchesQuery,
  StaticSiteBranchesQueryVariables,
} from '@/components/form/branch-selector/index.types.gen'

const GIT_BRANCHES_QUERY = gql`
  query GitBranches($idRemoteRepository: String!, $gitType: GitType!) {
    user {
      id
      repository(idRemoteRepository: $idRemoteRepository, gitType: $gitType) {
        id
        branches {
          name
        }
      }
    }
  }
`

const APP_BRANCHES_QUERY = gql`
  query AppBranches($appName: String!) {
    app(name: $appName) {
      id
      repository {
        id
        branches {
          name
        }
      }
    }
  }
`

const STATIC_SITE_BRANCHES_QUERY = gql`
  query StaticSiteBranches($staticSiteName: String!) {
    staticSite(name: $staticSiteName) {
      id
      repository {
        id
        branches {
          name
        }
      }
    }
  }
`

export const BranchSelectorContent = () => {
  const [open, setOpen] = useState(false)
  const form = useFormContext()
  const { watch, resetField } = form
  const { appName = '', staticSiteName = '' } = useParams<{
    appName?: string
    staticSiteName?: string
  }>()

  const defaultBranch = watch('defaultBranch')
  const idRemoteRepository = watch('idRemoteRepository')
  const gitType = watch('gitType')

  const { data, refetch } = useSuspenseQuery<
    GitBranchesQuery,
    GitBranchesQueryVariables
  >(
    GIT_BRANCHES_QUERY,
    idRemoteRepository && gitType
      ? {
          variables: { idRemoteRepository, gitType },
        }
      : skipToken
  )

  const { data: appBranchData, refetch: refetchAppBranches } = useSuspenseQuery<
    AppBranchesQuery,
    AppBranchesQueryVariables
  >(
    APP_BRANCHES_QUERY,
    appName
      ? {
          variables: { appName },
        }
      : skipToken
  )

  const { data: staticSiteBranchData, refetch: refetchStaticSiteBranches } =
    useSuspenseQuery<StaticSiteBranchesQuery, StaticSiteBranchesQueryVariables>(
      STATIC_SITE_BRANCHES_QUERY,
      staticSiteName
        ? {
            variables: { staticSiteName },
          }
        : skipToken
    )

  const branches = useMemo(() => {
    let branches: string[] = []
    if (appBranchData?.app?.repository?.branches?.length) {
      branches =
        appBranchData.app.repository.branches?.map(
          (branch) => branch?.name ?? ''
        ) ?? []
    } else if (staticSiteBranchData?.staticSite?.repository?.branches?.length) {
      branches =
        staticSiteBranchData.staticSite.repository.branches?.map(
          (branch) => branch?.name ?? ''
        ) ?? []
    } else {
      branches =
        data?.user?.repository?.branches?.map((branch) => branch?.name ?? '') ??
        []
    }

    return branches.filter(Boolean).sort((a, b) => {
      if (a === 'main' || a === 'master') {
        return -1
      } else if (b === 'main' || b === 'master') {
        return 1
      }
      return a.localeCompare(b)
    })
  }, [data, appBranchData, staticSiteBranchData])

  useEffect(() => {
    const mainBranch = branches?.find(
      (branch) => branch === 'main' || branch === 'master'
    )
    if (!defaultBranch && mainBranch && idRemoteRepository) {
      form.setValue('defaultBranch', mainBranch)
    }
  }, [idRemoteRepository, branches, defaultBranch, form, resetField])

  useEffect(() => {
    return () => {
      resetField('defaultBranch')
    }
  }, [resetField])

  const BranchComboBox = ({ children }: { children: ReactNode }) => (
    <FormField
      control={form.control}
      name="defaultBranch"
      render={({ field }) => (
        <FormItem className="w-full min-w-0">
          <FormLabel>Branch</FormLabel>
          <FormControl>
            <div className="flex flex-col gap-4 w-full min-w-0">
              <Popover open={open} onOpenChange={setOpen} modal>
                <div className="grid grid-cols-[1fr_auto] gap-4">
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      disabled={!idRemoteRepository}
                      variant="outline"
                      role="combobox"
                      aria-expanded={open}
                      className="w-full justify-between hover:bg-transparent data-[state=open]:bg-transparent min-w-0"
                    >
                      {defaultBranch ? (
                        <div className="flex items-center justify-between gap-2 w-full min-w-0">
                          <span
                            className="truncate min-w-0"
                            title={defaultBranch}
                          >
                            {defaultBranch}
                          </span>
                          <ChevronsUpDown className="shrink-0 opacity-50" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between w-full">
                          <span className="truncate">Select a branch</span>
                          <ChevronsUpDown className="ml-auto opacity-50" />
                        </div>
                      )}
                    </Button>
                  </PopoverTrigger>
                  {children}
                </div>
                <PopoverContent className="w-[var(--radix-popper-anchor-width)] p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search branch..."
                      className="h-9"
                    />
                    <CommandList>
                      <CommandEmpty>No branch found</CommandEmpty>
                      <CommandGroup>
                        {branches.map((branch) => {
                          const isSelected = branch === defaultBranch
                          return (
                            <CommandItem
                              className="cursor-pointer"
                              key={branch}
                              disabled={isSelected}
                              value={branch}
                              onSelect={() => {
                                field.onChange(branch)
                                setOpen(false)
                              }}
                            >
                              {branch}
                              <Check
                                className={cn(
                                  'ml-auto',
                                  isSelected ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                            </CommandItem>
                          )
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )

  return (
    <BranchComboBox>
      <Button
        disabled={!idRemoteRepository}
        variant="secondary"
        type="button"
        size="icon"
        aria-label="Refresh branches"
        onClick={() => {
          if (appName) {
            refetchAppBranches()
          } else if (staticSiteName) {
            refetchStaticSiteBranches()
          } else {
            refetch()
          }
        }}
      >
        <RefreshCw className="size-4" />
      </Button>
    </BranchComboBox>
  )
}

export const BranchSelector = () => {
  return (
    <Suspense fallback={<BranchSelectorFallback />}>
      <BranchSelectorContent />
    </Suspense>
  )
}
